/**
 * Client-side storage utilities for saved payment methods.
 * 
 * This module provides localStorage-based storage for payment method IDs
 * that were successfully saved on this browser/device. It supports the
 * "only_client_saved_payment_methods" security feature.
 * 
 * Storage key format: galleon_saved_pms_{appId}_{customerId}
 */

const STORAGE_KEY_PREFIX = 'galleon_saved_pms';

/**
 * Generates the localStorage key for a specific app and customer combination.
 */
function getStorageKey(appId: string, customerId: string): string {
  // Normalize appId to be safe for localStorage keys
  const normalizedAppId = appId.replace(/[^a-zA-Z0-9_-]/g, '_');
  const normalizedCustomerId = customerId.replace(/[^a-zA-Z0-9_-]/g, '_');
  return `${STORAGE_KEY_PREFIX}_${normalizedAppId}_${normalizedCustomerId}`;
}

/**
 * Gets the list of saved payment method IDs for a specific app and customer.
 * 
 * @param appId - The application ID
 * @param customerId - The Stripe customer ID
 * @returns Array of payment method IDs stored in localStorage, empty array if none
 */
export function getSavedPaymentMethodIds(appId: string, customerId: string): string[] {
  try {
    const key = getStorageKey(appId, customerId);
    const stored = localStorage.getItem(key);
    
    if (!stored) {
      return [];
    }
    
    const parsed = JSON.parse(stored);
    
    // Validate that it's an array of strings
    if (!Array.isArray(parsed)) {
      console.warn('[SavedPM Storage] Invalid storage format, resetting');
      localStorage.removeItem(key);
      return [];
    }
    
    // Filter out any non-string values
    return parsed.filter((id): id is string => typeof id === 'string');
  } catch (error) {
    console.error('[SavedPM Storage] Failed to get saved payment methods:', error);
    return [];
  }
}

/**
 * Adds a payment method ID to the stored list for a specific app and customer.
 * Duplicates are automatically prevented.
 * 
 * @param appId - The application ID
 * @param customerId - The Stripe customer ID
 * @param paymentMethodId - The payment method ID to add (e.g., "pm_xxx")
 */
export function addSavedPaymentMethodId(
  appId: string,
  customerId: string,
  paymentMethodId: string
): void {
  try {
    if (!paymentMethodId || typeof paymentMethodId !== 'string') {
      console.warn('[SavedPM Storage] Invalid payment method ID provided');
      return;
    }
    
    const key = getStorageKey(appId, customerId);
    const existing = getSavedPaymentMethodIds(appId, customerId);
    
    // Don't add duplicates
    if (existing.includes(paymentMethodId)) {
      return;
    }
    
    const updated = [...existing, paymentMethodId];
    localStorage.setItem(key, JSON.stringify(updated));
  } catch (error) {
    console.error('[SavedPM Storage] Failed to add payment method:', error);
  }
}

/**
 * Removes a payment method ID from the stored list for a specific app and customer.
 * 
 * @param appId - The application ID
 * @param customerId - The Stripe customer ID
 * @param paymentMethodId - The payment method ID to remove
 */
export function removeSavedPaymentMethodId(
  appId: string,
  customerId: string,
  paymentMethodId: string
): void {
  try {
    const key = getStorageKey(appId, customerId);
    const existing = getSavedPaymentMethodIds(appId, customerId);
    
    const updated = existing.filter((id) => id !== paymentMethodId);
    
    if (updated.length === 0) {
      localStorage.removeItem(key);
    } else {
      localStorage.setItem(key, JSON.stringify(updated));
    }
  } catch (error) {
    console.error('[SavedPM Storage] Failed to remove payment method:', error);
  }
}

/**
 * Clears all saved payment method IDs for a specific app and customer.
 * 
 * @param appId - The application ID
 * @param customerId - The Stripe customer ID
 */
export function clearSavedPaymentMethodIds(appId: string, customerId: string): void {
  try {
    const key = getStorageKey(appId, customerId);
    localStorage.removeItem(key);
  } catch (error) {
    console.error('[SavedPM Storage] Failed to clear payment methods:', error);
  }
}

/**
 * Checks if a specific payment method ID is stored for a given app and customer.
 * 
 * @param appId - The application ID
 * @param customerId - The Stripe customer ID
 * @param paymentMethodId - The payment method ID to check
 * @returns true if the payment method is stored, false otherwise
 */
export function hasSavedPaymentMethodId(
  appId: string,
  customerId: string,
  paymentMethodId: string
): boolean {
  try {
    const existing = getSavedPaymentMethodIds(appId, customerId);
    return existing.includes(paymentMethodId);
  } catch (error) {
    console.error('[SavedPM Storage] Failed to check payment method:', error);
    return false;
  }
}

